import React, { useState, useEffect } from 'react';

const ActiveTicketsTable = ({ activeTickets, primaryTicket }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [frozenTimers, setFrozenTimers] = useState({});

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Sort tickets by timestamp (most recent first)
  const sortedTickets = [...activeTickets].sort((a, b) =>
    new Date(b.timestamp) - new Date(a.timestamp)
  );



  // Helper function to format date as m/d (no leading zeros)
  const formatDateMD = (dateString) => {
    const date = new Date(dateString);
    const month = date.getMonth() + 1; // getMonth() returns 0-11
    const day = date.getDate();
    return `${month}/${day}`;
  };

  // Helper function to format time as hh:mm am/pm
  const formatTimeAMPM = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Helper function to get the most current status from ticket events
  const getCurrentStatus = (ticket) => {
    if (ticket && Array.isArray(ticket.events) && ticket.events.length > 0) {
      // Find the event with the latest event_date
      const latestEvent = ticket.events.reduce((a, b) => (new Date(a.event_date) > new Date(b.event_date) ? a : b));

      // Debug logging removed to prevent console spam

      return latestEvent.status;
    }
    // Fallback to ticket status if no events exist
    return ticket.status;
  };

  // Helper function to check if ticket is at stage 4 (completion)
  const isStage4Completed = (ticket) => {
    const currentStatus = getCurrentStatus(ticket)?.toLowerCase();
    return currentStatus === 'resolved' || !ticket.is_active;
  };

  // Helper function to get completion time (when ticket transitioned from alert to non-alert state)
  const getCompletionTime = (ticket) => {
    if (!ticket || !Array.isArray(ticket.events) || ticket.events.length === 0) {
      return null;
    }

    // Sort events by date to find the transition
    const sortedEvents = [...ticket.events].sort((a, b) => new Date(a.event_date) - new Date(b.event_date));

    // Find the last alert state event and the first non-alert state event
    const alertStates = ['alerted', 'acknowledged', 'evaluated'];
    let lastAlertEvent = null;
    let firstNonAlertEvent = null;

    for (const event of sortedEvents) {
      const status = event.status?.toLowerCase();
      if (alertStates.includes(status)) {
        lastAlertEvent = event;
      } else if (!alertStates.includes(status) && !firstNonAlertEvent) {
        firstNonAlertEvent = event;
        break; // Found the transition point
      }
    }

    // If we found a non-alert event, use its timestamp as completion time
    if (firstNonAlertEvent) {
      return new Date(firstNonAlertEvent.event_date);
    }

    // Fallback to ticket timestamp if no clear transition found
    return new Date(ticket.timestamp);
  };

  // Helper function to calculate timer value (HH:MM:SS format)
  const calculateTimerValue = (ticket) => {
    // Check if this ticket is at stage 4 completion
    if (isStage4Completed(ticket)) {
      // If we have a frozen timer value for this ticket, return it
      if (frozenTimers[ticket.ticket_id]) {
        return frozenTimers[ticket.ticket_id];
      }

      // Calculate and freeze the timer value at the moment of completion
      // The timer should count down from 10 minutes from when the ticket was first opened (alerted)
      let ticketOpenTime = null;

      if (ticket && Array.isArray(ticket.events) && ticket.events.length > 0) {
        // Find the first event (alerted) to get the ticket opening time
        const firstEvent = ticket.events.reduce((a, b) => (new Date(a.event_date) < new Date(b.event_date) ? a : b));
        ticketOpenTime = new Date(firstEvent.event_date);
      } else {
        // Fallback to ticket timestamp if no events
        ticketOpenTime = new Date(ticket.timestamp);
      }

      if (ticketOpenTime) {
        // Calculate target time as 10 minutes from when ticket was opened
        const targetTime = new Date(ticketOpenTime.getTime() + 10 * 60 * 1000);

        // Get the completion time (when ticket transitioned from alert to non-alert state)
        const completionTime = getCompletionTime(ticket);

        if (completionTime) {
          const diffMs = targetTime - completionTime;

          // When diffMs > 0, completion was BEFORE target time (time remaining/positive)
          // When diffMs < 0, completion was AFTER target time (overdue/negative)
          const isNegative = diffMs < 0;
          const absDiffMs = Math.abs(diffMs);

          const hours = Math.floor(absDiffMs / (1000 * 60 * 60));
          const minutes = Math.floor((absDiffMs % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((absDiffMs % (1000 * 60)) / 1000);

          const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
          const result = isNegative ? `-${timeString}` : timeString;

          // Store the frozen timer value
          setFrozenTimers(prev => ({
            ...prev,
            [ticket.ticket_id]: result
          }));

          return result;
        }
      }

      return '00:00:00'; // fallback
    }

    // For active tickets (not stage 4), calculate normally
    // The timer should count down from 10 minutes from when the ticket was first opened (alerted)
    let ticketOpenTime = null;

    if (ticket && Array.isArray(ticket.events) && ticket.events.length > 0) {
      // Find the first event (alerted) to get the ticket opening time
      const firstEvent = ticket.events.reduce((a, b) => (new Date(a.event_date) < new Date(b.event_date) ? a : b));
      ticketOpenTime = new Date(firstEvent.event_date);
    } else {
      // Fallback to ticket timestamp if no events
      ticketOpenTime = new Date(ticket.timestamp);
    }

    if (ticketOpenTime) {
      // Calculate target time as 10 minutes from when ticket was opened
      const targetTime = new Date(ticketOpenTime.getTime() + 10 * 60 * 1000);
      const diffMs = targetTime - currentTime;

      // When diffMs > 0, current time is BEFORE target time (time remaining/positive)
      // When diffMs < 0, current time is AFTER target time (overdue/negative)
      const isNegative = diffMs < 0;
      const absDiffMs = Math.abs(diffMs);

      const hours = Math.floor(absDiffMs / (1000 * 60 * 60));
      const minutes = Math.floor((absDiffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((absDiffMs % (1000 * 60)) / 1000);

      const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      const result = isNegative ? `-${timeString}` : timeString;

      // Debug logging removed to prevent console spam

      return result;
    }

    return '00:00:00'; // fallback
  };

  return (
    <div className="active-tickets-table">
      <table className="tickets-table">
        <thead>
          <tr>
            <th>Job # / Ticket #</th>
            <th>Date / Time</th>
            <th>Workcenter</th>
          </tr>
        </thead>
        <tbody>
          {/* Render actual ticket data rows */}
          {sortedTickets.map((ticket, index) => {
            const timerValue = calculateTimerValue(ticket);
            const isNegative = timerValue.startsWith('-');
            const isPrimaryTicket = primaryTicket && ticket.ticket_id === primaryTicket.ticket_id;

            return (
              <tr key={ticket.ticket_id || index}>
                <td className="job-ticket-number">
                  <div className="job-ticket-stack">
                    <span className="job-number">{ticket.job_number}</span>
                    <span className="ticket-number">{ticket.ticket_id}</span>
                  </div>
                </td>
                <td className="date-time">
                  <div className="date-time-stack">
                    <div className={`date-section ${isNegative ? 'negative' : ''}`}>
                      <span>{formatDateMD(ticket.timestamp)}</span>
                    </div>
                    <div className="time-section">
                      <div className={`timer-value ${isNegative ? 'negative' : ''}`}>
                        <span>{timerValue}</span>
                      </div>
                      <div className={`event-time ${isNegative ? 'negative' : ''}`}>
                        <span>{formatTimeAMPM(ticket.timestamp)}</span>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="location">
                  <div className="location-stack">
                    <span className={`side-indicator ${ticket.workcenter_code[2] && ticket.workcenter_code[2].toUpperCase() === 'C' ? 'side-c' : 'side-s'}`}>
                      {ticket.workcenter_code[2] && ticket.workcenter_code[2].toUpperCase() === 'C' ? 'C' : 'S'}
                    </span>
                    <span className="workcenter">
                      {ticket.workcenter_code}
                    </span>
                    <span className={`alert-status ${isPrimaryTicket ? 'primary-ticket' : ''}`}>
                      {(() => {
                        const currentStatus = getCurrentStatus(ticket)?.toLowerCase();
                        if (currentStatus === 'alerted') return '1';
                        if (currentStatus === 'acknowledged') return '2';
                        if (currentStatus === 'evaluated') return '3';
                        if (currentStatus === 'resolved' || !ticket.is_active) return '4';
                        return '1'; // default to alerted
                      })()}
                    </span>
                  </div>
                </td>
              </tr>
            );
          })}


        </tbody>
      </table>
    </div>
  );
};

export default ActiveTicketsTable;